<template>
  <div class="orden-cap-page">
    <ToastPrime position="top-right" />

    <header class="orden-cap-hero">
      <p class="orden-cap-eyebrow">Capítulos</p>
      <h1 class="orden-cap-title">Orden dentro de unidades</h1>
      <p class="orden-cap-lead">
        Elige curso y asignatura, indica el <strong>nuevo orden</strong> numérico para cada capítulo (grupo) y guarda por unidad.
      </p>
    </header>

    <CardPrime class="orden-cap-card mb-3">
      <template #title>
        <span class="orden-cap-card-title">
          <i class="pi pi-filter" aria-hidden="true" />
          Curso y asignatura
        </span>
      </template>
      <template #content>
        <div class="orden-cap-filters">
          <div class="orden-cap-field">
            <label class="orden-cap-label">Curso</label>
            <CursoDropdown
              v-model="selectedCurso"
              :mostrarErrores="mostrarErrores"
              tooltip="Selecciona un curso"
              @update:modelValue="onCursoChange"
            />
          </div>
          <div class="orden-cap-field">
            <label class="orden-cap-label">Asignatura</label>
            <AsignaturaDropdown
              v-model="selectedAsignatura"
              :mostrarErrores="mostrarErrores"
              tooltip="Selecciona una asignatura"
              @update:modelValue="onAsignaturaChange"
            />
          </div>
        </div>
        <p v-if="current_capitulo" class="orden-cap-toolbar-hint is-muted">
          {{ current_capitulo }}
        </p>
      </template>
    </CardPrime>

    <div v-if="subject" class="orden-cap-summary" role="status">
      <i class="pi pi-info-circle" aria-hidden="true" />
      <span>{{ subject.Cantidad_videos }} videos</span>
      <span class="orden-cap-dot" aria-hidden="true" />
      <span>{{ orderedGroupedChapters.length }} unidades</span>
    </div>

    <div v-if="!subject && selectedCurso.length && selectedAsignatura.length" class="orden-cap-empty">
      <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem" />
      <p>Cargando estructura de capítulos…</p>
    </div>

    <div v-else-if="subject && !orderedGroupedChapters.length" class="orden-cap-empty">
      <i class="pi pi-inbox" style="font-size: 1.75rem" />
      <p>No hay capítulos para mostrar con el curso y asignatura seleccionados.</p>
    </div>

    <div
      v-for="(unit, index) in orderedGroupedChapters"
      :key="unit.unidad"
      class="orden-cap-unit"
    >
      <div class="orden-cap-unit-head">
        <div>
          <h2 class="orden-cap-unit-title">{{ getUnidadLabel(index, unit.unidad) }}</h2>
          <p class="orden-cap-unit-meta">
            {{ unit.grupos.length }} capítulo(es) ·
            {{
              unit.grupos.reduce(
                (acc, curr) =>
                  acc + curr.capitulos.reduce((total, cap) => total + getVideosCap(cap.IdCapitulo).length, 0),
                0
              )
            }}
            videos
          </p>
        </div>
        <ButtonPrime
          label="Guardar orden de la unidad"
          icon="pi pi-save"
          :loading="savingUnitKey === unitKey(unit)"
          @click="updateUnitOrder(unit)"
        />
      </div>

      <ul class="orden-cap-grupos" role="list">
        <li v-for="grupo in unit.grupos" :key="grupo.grupo" class="orden-cap-grupo">
          <div class="orden-cap-grupo-label" :title="grupo.grupo">
            <span class="orden-cap-grupo-name">Capítulo</span>
            <span class="orden-cap-grupo-text">{{ grupo.grupo }}</span>
          </div>
          <div class="orden-cap-grupo-input">
            <label class="sr-only" :for="'orden-' + slugKey(grupo.grupo)">Nuevo orden</label>
            <InputText
              :id="'orden-' + slugKey(grupo.grupo)"
              v-model="grupo.newOrden"
              type="number"
              min="1"
              class="w-full"
              inputmode="numeric"
              placeholder="Nº orden"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SobjectService from '@/services/SobjectService';
import CursoDropdown from '@/components/selectores/CursoDropdown.vue';
import AsignaturaDropdown from '@/components/selectores/AsignaturaDropdown.vue';
import CardPrime from 'primevue/card';
import ButtonPrime from 'primevue/button';
import InputText from 'primevue/inputtext';
import ToastPrime from 'primevue/toast';

export default {
  name: 'chapters-subject-page',
  components: {
    CursoDropdown,
    AsignaturaDropdown,
    CardPrime,
    ButtonPrime,
    InputText,
    ToastPrime,
  },
  data() {
    return {
      subject: null,
      orderedGroupedChapters: [],
      selectedCurso: [],
      selectedAsignatura: [],
      mostrarErrores: false,
      savingUnitKey: null,
    };
  },
  methods: {
    unitKey(unit) {
      return unit?.unidad || 'unit';
    },
    slugKey(text) {
      return String(text || 'x').replace(/[^a-z0-9]+/gi, '-').slice(0, 40);
    },
    toast(severity, summary, detail) {
      this.$toast.add({ severity, summary, detail, life: severity === 'error' ? 6000 : 4000 });
    },
    parsePrefix(str) {
      const match = str.match(/^(I|II|III|IV|V|VI|VII|VIII|IX|X|\d+)/);
      if (!match) return 0;
      const pre = match[0];
      if (/\d+/.test(pre)) return parseInt(pre, 10);
      const romanToInt = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8, IX: 9, X: 10 };
      return romanToInt[pre] || 0;
    },
    removePrefix(str) {
      return str.replace(/^(I|II|III|IV|V|VI|VII|VIII|IX|X|\d+)[.:]?\s*/, '').trim();
    },
    getUnidadLabel(index, unidad) {
      const nuevaUnidad = unidad.replace(/^(I|II|III|IV|V|VI|VII|VIII|IX|X)\./, '').trim();
      if (nuevaUnidad.toLowerCase().startsWith('unidad')) {
        return nuevaUnidad;
      }
      return `Unidad ${index + 1}: ${nuevaUnidad}`;
    },
    getVideosCap(idd) {
      return this.subject?.VideosCapitulo.filter(x => x.IdCapitulo === idd) || [];
    },
    getChapters(id_curso, id_asignatura) {
      this.$store.dispatch('form/submit', true);
      return SobjectService.getDataSubjects(id_curso, id_asignatura)
        .then(response => {
          this.subject = response.data;
          const groupedByUnit = this.subject.Capitulos.reduce((units, chapter) => {
            const unitKey = chapter.UnidadGrupo;
            if (!units[unitKey]) units[unitKey] = {};
            const groupKey = chapter.GlosaGrupo;
            if (!units[unitKey][groupKey]) units[unitKey][groupKey] = [];
            units[unitKey][groupKey].push(chapter);
            return units;
          }, {});

          Object.keys(groupedByUnit).forEach(unitKey => {
            Object.keys(groupedByUnit[unitKey]).forEach(groupKey => {
              groupedByUnit[unitKey][groupKey].sort((a, b) => a.Orden - b.Orden);
            });
          });

          this.orderedGroupedChapters = Object.entries(groupedByUnit)
            .sort(([u1], [u2]) => this.parsePrefix(u1) - this.parsePrefix(u2))
            .map(([unidad, grupos]) => ({
              unidad,
              grupos: Object.entries(grupos)
                .sort(([g1], [g2]) => this.parsePrefix(g1) - this.parsePrefix(g2))
                .map(([grupo, capitulos]) => ({ grupo, capitulos, newOrden: '' })),
            }));
          this.$store.dispatch('form/submit', false);
        })
        .catch(error => {
          console.error('Error al obtener los capítulos:', error);
          this.toast('error', 'Error', 'No se pudieron cargar los capítulos.');
          this.$store.dispatch('form/submit', false);
        });
    },
    onCursoChange(newValue) {
      this.selectedCurso = newValue;
      if (this.selectedCurso.length && this.selectedAsignatura.length) {
        this.getChapters(this.selectedCurso[0], this.selectedAsignatura[0]);
      } else {
        this.subject = null;
        this.orderedGroupedChapters = [];
      }
    },
    onAsignaturaChange(newValue) {
      this.selectedAsignatura = newValue;
      if (this.selectedCurso.length && this.selectedAsignatura.length) {
        this.getChapters(this.selectedCurso[0], this.selectedAsignatura[0]);
      } else {
        this.subject = null;
        this.orderedGroupedChapters = [];
      }
    },
    async updateUnitOrder(unit) {
      const key = this.unitKey(unit);
      const invalidGroups = unit.grupos.filter(
        grupo => grupo.newOrden === '' || grupo.newOrden === null || isNaN(Number(grupo.newOrden)) || Number(grupo.newOrden) <= 0
      );
      if (invalidGroups.length > 0) {
        this.toast('warn', 'Orden incompleto', 'Cada capítulo necesita un número de orden entero positivo.');
        return;
      }

      this.savingUnitKey = key;
      try {
        for (const grupo of unit.grupos) {
          const newPrefix = `${grupo.newOrden}: `;
          const baseGrupo = this.removePrefix(grupo.grupo);
          const newGrupo = `${newPrefix}${baseGrupo}`;

          for (const cap of grupo.capitulos) {
            const baseGlosa = this.removePrefix(cap.Glosa);
            const newGlosa = `${newPrefix}${baseGlosa}`;
            const payload = {
              IdCapitulo: cap.IdCapitulo,
              Glosa: newGlosa,
              GlosaGrupo: newGrupo,
            };
            await SobjectService.updateChapter(payload);
            cap.Glosa = newGlosa;
            cap.GlosaGrupo = newGrupo;
          }
          grupo.grupo = newGrupo;
        }
        await this.getChapters(this.selectedCurso[0], this.selectedAsignatura[0]);
        this.toast('success', 'Guardado', 'Orden de la unidad actualizado correctamente.');
      } catch (error) {
        console.error('Error actualizando orden de la unidad:', error);
        this.toast('error', 'Error al guardar', 'No se pudo actualizar. Revisa la conexión e inténtalo de nuevo.');
      } finally {
        this.savingUnitKey = null;
      }
    },
  },
  created() {
    if (this.$route.params.curso_id && this.$route.params.asignatura_id) {
      this.selectedCurso = [this.$route.params.curso_id];
      this.selectedAsignatura = [this.$route.params.asignatura_id];
      this.getChapters(this.selectedCurso[0], this.selectedAsignatura[0]);
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      token: 'auth/access-token',
      current_capitulo: 'toolbar/title',
    }),
  },
};
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.orden-cap-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 0 1.5rem;
}

.orden-cap-hero {
  margin-bottom: 1.5rem;
}

.orden-cap-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--admin-text-muted, #64748b);
  margin: 0 0 0.4rem;
}

.orden-cap-title {
  font-size: clamp(1.35rem, 3vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--admin-text, #0f172a);
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.orden-cap-lead {
  margin: 0;
  max-width: 40rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--admin-text-muted, #64748b);
}

.orden-cap-lead :deep(strong) {
  color: var(--admin-text, #334155);
  font-weight: 600;
}

.orden-cap-card :deep(.p-card) {
  border-radius: var(--admin-radius, 14px);
  border: 1px solid var(--admin-border, rgba(15, 23, 42, 0.08));
  box-shadow: var(--admin-shadow, 0 8px 24px rgba(15, 23, 42, 0.06));
  background: #fff;
}

.orden-cap-card :deep(.p-card-title) {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--admin-text, #1e293b);
}

.orden-cap-card-title {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.orden-cap-card-title .pi {
  color: var(--primary-color);
  font-size: 1rem;
}

.orden-cap-filters {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.1rem;
}

@media (min-width: 640px) {
  .orden-cap-filters {
    grid-template-columns: 1fr 1fr;
  }
}

.orden-cap-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.orden-cap-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--admin-text, #334155);
}

.orden-cap-toolbar-hint {
  margin: 1rem 0 0;
  font-size: 0.8rem;
  color: var(--admin-text-muted, #94a3b8);
}

.orden-cap-toolbar-hint.is-muted {
  font-style: normal;
}

.orden-cap-summary {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.6rem;
  margin-bottom: 1.1rem;
  padding: 0.5rem 0.75rem;
  background: #f1f5f9;
  border: 1px solid var(--admin-border, #e2e8f0);
  border-radius: 10px;
  font-size: 0.82rem;
  color: var(--admin-text, #475569);
}

.orden-cap-summary .pi {
  color: var(--primary-color);
  font-size: 0.9rem;
}

.orden-cap-dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #94a3b8;
  opacity: 0.6;
}

.orden-cap-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2.5rem 1.5rem;
  text-align: center;
  color: var(--admin-text-muted, #64748b);
  border: 1px dashed var(--admin-border, #cbd5e1);
  border-radius: var(--admin-radius, 14px);
  background: #fafbfc;
  margin-bottom: 1rem;
}

.orden-cap-empty p {
  margin: 0;
  font-size: 0.9rem;
  max-width: 22rem;
}

.orden-cap-unit {
  background: #fff;
  border: 1px solid var(--admin-border, rgba(15, 23, 42, 0.1));
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.orden-cap-unit-head {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  padding: 1.1rem 1.2rem 0.9rem;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  border-bottom: 1px solid var(--admin-border, #e2e8f0);
}

@media (min-width: 560px) {
  .orden-cap-unit-head {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
}

.orden-cap-unit-title {
  margin: 0;
  font-size: 1.02rem;
  font-weight: 600;
  color: var(--admin-text, #0f172a);
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.orden-cap-unit-meta {
  margin: 0.3rem 0 0;
  font-size: 0.8rem;
  color: var(--admin-text-muted, #64748b);
}

.orden-cap-grupos {
  list-style: none;
  margin: 0;
  padding: 0.35rem 0.5rem 0.6rem;
}

.orden-cap-grupo {
  display: grid;
  grid-template-columns: 1fr minmax(5.5rem, 7.5rem);
  gap: 0.75rem 1rem;
  align-items: start;
  padding: 0.6rem 0.65rem;
  border-bottom: 1px solid #f1f5f9;
}

.orden-cap-grupo:last-child {
  border-bottom: none;
}

.orden-cap-grupo-label {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.orden-cap-grupo-name {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--admin-text-muted, #94a3b8);
}

.orden-cap-grupo-text {
  font-size: 0.85rem;
  color: var(--admin-text, #334155);
  line-height: 1.35;
  word-break: break-word;
}

.orden-cap-grupo-input {
  min-width: 0;
}

.orden-cap-grupo-input :deep(.p-inputtext) {
  width: 100%;
  font-size: 0.9rem;
  border-radius: 8px;
}
</style>
